<template>
  <validation-provider #default="validationContext" :name="label" :rules="rules">
    <b-form-group
      :label="label"
      :label-for="id"
      :description="description"
      :state="getValidationState(validationContext)"
    >
      <v-select
        :id="id"
        v-model="inputValue"
        :options="options"
        :placeholder="placeholder"
        :reduce="reduceOption"
        :clearable="clearable"
        :multiple="multiple"
        :taggable="taggable"
        :push-tags="pushTags"
        :create-option="createOption"
        :label="optionLabel"
        :get-option-label="getOptionLabel"
        append-to-body
        :calculate-position="withPopper"
      />
      <b-form-invalid-feedback :state="getValidationState(validationContext)">
        {{ validationContext.errors[0] }}
      </b-form-invalid-feedback>
    </b-form-group>
  </validation-provider>
</template>

<script>
import { ref, watch } from "@vue/composition-api";
import formValidation from "@core/comp-functions/forms/form-validation";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "FormFieldSelect",
  components: {
    vSelect,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    value: {
      type: [String, Number, Array],
      required: false,
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    rules: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    pushTags: {
      type: Boolean,
      default: true,
    },
    reduceOption: {
      type: Function,
      default: (option) => option.value,
    },
    optionLabel: {
      type: String,
      default: "text",
    },
  },
  setup(props, { emit }) {
    const { getValidationState } = formValidation();
    const inputValue = ref(
      props.value === null || props.value === undefined
        ? props.multiple
          ? []
          : ""
        : props.value
    );

    watch(
      () => props.value,
      (newValue) => {
        const processedValue =
          newValue === null || newValue === undefined
            ? props.multiple
              ? []
              : ""
            : newValue;

        if (JSON.stringify(inputValue.value) !== JSON.stringify(processedValue)) {
          inputValue.value = processedValue;
        }
      },
      { immediate: true }
    );

    watch(
      () => inputValue.value,
      (newValue) => {
        emit("input", newValue);
      }
    );

    const createOption = (newOption) => {
      if (props.options.length > 0 && typeof props.options[0] === 'object') {
        const labelKey = props.optionLabel || 'text';
        const valueKey = props.optionLabel === 'name' ? 'id' : 'value';
        return { [labelKey]: newOption, [valueKey]: newOption };
      }
      return newOption;
    };

    const getOptionLabel = (option) => {
      if (typeof option === 'object' && option !== null) {
        return option[props.optionLabel] || option.text || option.label || option.value || option;
      }
      return option;
    };

    return {
      inputValue,
      getValidationState,
      createOption,
      getOptionLabel,
      withPopper: popperHelper.withPopper,
    };
  },
};
</script>

<style lang="scss"></style>
